/**
 * API Module - Central exports for all API functionality
 * Re-exports the main API client and utilities
 */

// Re-export types and classes
export { type ApiResponse } from '../../utils/api-client'

// Re-export handleApiError from utils
export { handleApiError } from '../utils'

// Re-export the convenience API object
export { api } from '../../utils/api-client'

// Create extended API client with domain-specific methods
import baseApiClient from '../../utils/api-client'
import type { ApiResponse } from '../../utils/api-client'

/**
 * Extended API client with domain-specific methods
 * This extends the base API client with methods that were previously available
 */
export class ExtendedApiClient {
  private client = baseApiClient

  // Candidate Applications
  async getCandidateApplications(email?: string): Promise<ApiResponse> {
    const params = email ? { email } : {}
    return this.client.get('/applications/candidate-applications', params)
  }

  // Dashboard
  async getDashboardStats(period?: string): Promise<ApiResponse> {
    const params = period ? { period } : {}
    return this.client.get('/dashboard/stats', params)
  }

  // Positions
  async getPositions(params?: any): Promise<ApiResponse> {
    // Reuse the base client's Mangum-safe endpoint
    return this.client.getPositions(params)
  }

  async getPosition(id: string): Promise<ApiResponse> {
    return this.client.get(`/positions/${id}`)
  }

  async getPositionCandidates(positionId: string, params?: any): Promise<ApiResponse> {
    return this.client.get(`/candidates/position/${positionId}`, params)
  }

  async getPositionInterviews(positionId: string, params?: any): Promise<ApiResponse> {
    return this.client.get(`/interviews/position/${positionId}`, params)
  }

  async getPositionAnalytics(id: string): Promise<ApiResponse> {
    return this.client.get(`/positions/${id}/analytics`)
  }

  // Candidates
  async getCandidates(params?: any): Promise<ApiResponse> {
    return this.client.get('/candidates/list', params)
  }

  async getCandidate(id: string): Promise<ApiResponse> {
    return this.client.get(`/candidates/${id}`)
  }

  // Teams
  async getTeams(): Promise<ApiResponse> {
    // Defer to base client to ensure we hit the Mangum-safe /teams/list endpoint
    return this.client.getTeams()
  }

  async getTeamDetails(id: string): Promise<ApiResponse> {
    return this.client.get(`/teams/${id}`)
  }

  async getTeamMembers(teamId: string): Promise<ApiResponse> {
    return this.client.get(`/teams/${teamId}/members`)
  }

  async getTeamSharedPositions(teamId: string): Promise<ApiResponse> {
    return this.client.get(`/position-shares/team/${teamId}`)
  }

  // Invitations
  async getInvitationDetails(token: string): Promise<ApiResponse> {
    return this.client.get(`/teams/invite/${token}`)
  }

  // Public Jobs
  async getPublicJobs(params?: any): Promise<ApiResponse> {
    // Use Mangum-safe list endpoint
    return this.client.getPublicJobs(params)
  }

  // Coding Problems
  async getCodingProblems(params?: any): Promise<ApiResponse> {
    return this.client.get('/coding-problems/list', params)
  }

  async getCodingProblemCategories(): Promise<ApiResponse> {
    return this.client.get('/coding-problems/categories')
  }

  // Skills
  async getSkills(params?: any): Promise<ApiResponse> {
    return this.client.get('/skills/list', params)
  }

  // Database Challenges
  async getDatabaseChallenges(params?: any): Promise<ApiResponse> {
    return this.client.get('/database-challenges', params)
  }

  async submitDatabaseChallenge(data: any): Promise<ApiResponse> {
    return this.client.submitDatabaseChallenge(data)
  }

  // Users
  async getUsers(params?: any): Promise<ApiResponse> {
    return this.client.get('/users/list', params)
  }

  async getAdminRecruiterApplicants(params?: any): Promise<ApiResponse> {
    return this.client.get('/users/admin/recruiters/applicants', params)
  }

  async updateRecruiterStatus(userUuid: string, status: 'pending_review' | 'approved' | 'hold', message?: string): Promise<ApiResponse> {
    return this.client.updateRecruiterStatus(userUuid, status, message)
  }

  async messageRecruiterApplicant(userUuid: string, payload: { message: string; subject?: string }): Promise<ApiResponse> {
    return this.client.messageRecruiterApplicant(userUuid, payload)
  }

  async getCurrentUser(): Promise<ApiResponse> {
    // FastAPI exposes the current-user helper at /users/me
    return this.client.get('/users/me')
  }

  // Authentication
  async login(email: string, password: string): Promise<ApiResponse> {
    return this.client.post('/auth/login', { email, password })
  }

  async googleLogin(credential: string): Promise<ApiResponse> {
    return this.client.post('/auth/google-login', { credential })
  }

  // Interviews
  async getInterviews(params?: any): Promise<ApiResponse> {
    return this.client.get('/interviews/list', params)
  }

  async scheduleInterview(data: any): Promise<ApiResponse> {
    return this.client.scheduleInterview(data)
  }

  async scheduleVoiceInterview(data: any): Promise<ApiResponse> {
    return this.client.scheduleVoiceInterview(data)
  }

  async generateInterviewUrl(data: any): Promise<ApiResponse> {
    return this.client.generateInterviewUrl(data)
  }

  async getInterviewResponse(interviewId: string): Promise<ApiResponse> {
    return this.client.get(`/interviews/${interviewId}/response`)
  }

  async getInterviewDataModeling(interviewId: string, sessionId?: string): Promise<ApiResponse> {
    const params = sessionId ? { session_id: sessionId } : {}
    return this.client.get(`/interviews/${interviewId}/data-modeling`, params)
  }

  async getInterviewUmlDesign(interviewId: string, sessionId?: string): Promise<ApiResponse> {
    const params = sessionId ? { session_id: sessionId } : {}
    return this.client.get(`/interviews/${interviewId}/uml-design`, params)
  }

  async getInterviewSystemDesign(interviewId: string, sessionId?: string): Promise<ApiResponse> {
    const params = sessionId ? { session_id: sessionId } : {}
    return this.client.get(`/interviews/${interviewId}/system-design`, params)
  }

  async getInterviewDatabaseChallenges(interviewId: string, sessionId?: string): Promise<ApiResponse> {
    const params = sessionId ? { session_id: sessionId } : {}
    return this.client.get(`/interviews/${interviewId}/database-challenges`, params)
  }

  async getInterviewCodingChallenges(interviewId: string): Promise<ApiResponse> {
    return this.client.get(`/interviews/${interviewId}/coding-challenges`)
  }

  async getInterviewEvaluations(interviewId: string): Promise<ApiResponse> {
    return this.client.get(`/evaluations/candidate-evaluations/interview/${interviewId}`)
  }

  async getEvaluation(sourceTable: string, sourceRecordUuid: string): Promise<ApiResponse> {
    return this.client.get(`/evaluations/candidate-evaluations/${sourceTable}/${sourceRecordUuid}`)
  }

  async createEvaluation(data: any): Promise<ApiResponse> {
    return this.client.post('/evaluations/candidate-evaluations', data)
  }

  async updateEvaluation(evaluationUuid: string, data: any): Promise<ApiResponse> {
    return this.client.put(`/evaluations/candidate-evaluations/${evaluationUuid}`, data)
  }

  async updateInterview(interviewId: string, data: any): Promise<ApiResponse> {
    return this.client.put(`/interviews/${interviewId}`, data)
  }

  // Position operations
  async createPosition(data: any): Promise<ApiResponse> {
    return this.client.post('/positions', data)
  }

  async updatePosition(id: string, data: any): Promise<ApiResponse> {
    return this.client.put(`/positions/${id}`, data)
  }

  async sharePositionWithTeam(positionId: string, data: any): Promise<ApiResponse> {
    return this.client.post('/pos-shares', {
      position_id: positionId,
      share_type: 'team',
      target_id: data.teamId || data.team_id,
      notes: data.notes || data.message
    })
  }

  async sharePositionWithUser(positionId: string, data: any): Promise<ApiResponse> {
    return this.client.post('/pos-shares', {
      position_id: positionId,
      share_type: 'individual',
      target_id: data.userId || data.user_id,
      notes: data.notes || data.message
    })
  }

  async getPositionShares(positionId: string): Promise<ApiResponse> {
    return this.client.getPositionShares(positionId)
  }

  async removePositionShare(shareId: string | number): Promise<ApiResponse> {
    return this.client.removePositionShare(shareId)
  }

  // Candidate comparison
  async compareCandidates(candidateIds: string[]): Promise<ApiResponse> {
    return this.client.post('/candidates/compare', { candidateIds })
  }

  // Candidate operations
  async updateCandidate(id: string, data: any): Promise<ApiResponse> {
    return this.client.put(`/candidates/${id}`, data)
  }

  async validateCandidateEmail(email: string): Promise<ApiResponse> {
    return this.client.validateCandidateEmail(email)
  }

  async createCandidateFromForm(data: any): Promise<ApiResponse> {
    return this.client.createCandidateFromForm(data)
  }

  async followCandidate(candidateUuid: string): Promise<ApiResponse> {
    return this.client.followCandidate(candidateUuid)
  }

  async unfollowCandidate(candidateUuid: string): Promise<ApiResponse> {
    return this.client.unfollowCandidate(candidateUuid)
  }

  // Coding Challenge sharing
  async shareChallengeWithTeam(challengeId: string, data: any): Promise<ApiResponse> {
    return this.client.post(`/coding-challenges/${challengeId}/share/team`, data)
  }

  async shareChallengeWithUser(challengeId: string, data: any): Promise<ApiResponse> {
    return this.client.post(`/coding-challenges/${challengeId}/share/user`, data)
  }

  // Create coding problem
  async createCodingProblem(data: any): Promise<ApiResponse> {
    return this.client.post('/coding-problems', data)
  }

  // Team management methods
  async createTeam(data: any): Promise<ApiResponse> {
    return this.client.post('/teams', data)
  }

  async updateTeam(teamId: string, data: any): Promise<ApiResponse> {
    return this.client.put(`/teams/${teamId}`, data)
  }

  async deleteTeam(teamId: string): Promise<ApiResponse> {
    return this.client.delete(`/teams/${teamId}`)
  }

  async getTeamManagementData(teamId: string): Promise<ApiResponse> {
    return this.client.get(`/teams/${teamId}/management`)
  }

  async removeTeamMember(teamId: string, memberId: string): Promise<ApiResponse> {
    return this.client.delete(`/teams/${teamId}/members/${memberId}`)
  }

  async changeTeamMemberRole(teamId: string, memberId: string, newRole: string): Promise<ApiResponse> {
    return this.client.put(`/teams/${teamId}/members/${memberId}/role`, { role: newRole })
  }

  // Team invitation methods
  async getTeamInvitations(type: string): Promise<ApiResponse> {
    return this.client.get('/team-invitations', { type })
  }

  async sendTeamInvitation(data: any): Promise<ApiResponse> {
    return this.client.post('/team-invitations', data)
  }

  async acceptTeamInvitation(token: string, message?: string): Promise<ApiResponse> {
    return this.client.post(`/team-invitations/${token}/accept`, message ? { response_message: message } : {})
  }

  async declineTeamInvitation(token: string, message?: string): Promise<ApiResponse> {
    return this.client.post(`/team-invitations/${token}/decline`, message ? { response_message: message } : {})
  }

  // Interview sharing
  async shareInterviewWithTeam(interviewId: string, data: any): Promise<ApiResponse> {
    return this.client.post(`/interviews/${interviewId}/share/team`, data)
  }

  // Data export
  async exportData(type: string, params?: any): Promise<ApiResponse> {
    return this.client.get(`/export/${type}`, params)
  }

  // Resume parsing/upload for profile
  async uploadResumeForProfile(file: File, persist: boolean = false): Promise<ApiResponse> {
    return this.client.uploadResumeForProfile(file, persist)
  }

  // Generic request method for backward compatibility
  async request<T = any>(endpoint: string, options?: RequestInit): Promise<ApiResponse<T>> {
    const method = options?.method?.toUpperCase() || 'GET'
    const body = options?.body

    switch (method) {
      case 'GET':
        return this.client.get<T>(endpoint)
      case 'POST':
        return this.client.post<T>(endpoint, body ? JSON.parse(body as string) : undefined)
      case 'PUT':
        return this.client.put<T>(endpoint, body ? JSON.parse(body as string) : undefined)
      case 'DELETE':
        return this.client.delete<T>(endpoint)
      case 'PATCH':
        return this.client.patch<T>(endpoint, body ? JSON.parse(body as string) : undefined)
      default:
        return this.client.get<T>(endpoint)
    }
  }

  // Pass through all base client methods
  async get<T = any>(endpoint: string, params?: Record<string, any>): Promise<ApiResponse<T>> {
    return this.client.get<T>(endpoint, params)
  }

  async post<T = any>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    return this.client.post<T>(endpoint, data)
  }

  async put<T = any>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    return this.client.put<T>(endpoint, data)
  }

  async delete<T = any>(endpoint: string): Promise<ApiResponse<T>> {
    return this.client.delete<T>(endpoint)
  }

  async patch<T = any>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    return this.client.patch<T>(endpoint, data)
  }

  // Auth methods
  setAuthToken(token: string, persistent: boolean = true): void {
    return this.client.setAuthToken(token, persistent)
  }

  clearAuthToken(): void {
    return this.client.clearAuthToken()
  }

  getBaseUrl(): string {
    return this.client.getBaseUrl()
  }

  setBaseUrl(url: string): void {
    return this.client.setBaseUrl(url)
  }
}

// Create and export the extended client instance
const apiClient = new ExtendedApiClient()

// Export both default and named exports
export default apiClient
export { apiClient }
